import { useEffect } from 'react'
import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Info, Settings } from 'lucide-react'
import type { CommunityInput, SavedCommunity } from '@creanizer/types'
import { generateCharacterContext, generateEnemyContext, generatePlotContext, generateItemContext, generateDungeonContext } from '@creanizer/utils'
import { useCharacterStore } from '../../../../../stores/characterStore'
import { usePlotStore } from '../../../../../stores/plotStore'
import { useEnemyStore } from '../../../../../stores/enemyStore'
import { useItemStore } from '../../../../../stores/itemStore'
import { useDungeonStore } from '../../../../../stores/dungeonStore'
import { useWorldBuilderStore } from '../../../../../stores/worldbuilderStore'
import { useCommunityStore } from '../../../../../stores/communityStore'
import { CommunityContextForm } from './CommunityContextForm'

interface ReferenceModeFormProps {
  input: CommunityInput
  onChange: (input: CommunityInput) => void
}

export function ReferenceModeForm({ input, onChange }: ReferenceModeFormProps) {
  const { characters } = useCharacterStore()
  const { savedPlots } = usePlotStore()
  const { enemies: savedEnemies, fixDuplicateIds } = useEnemyStore()
  const { items: savedItems } = useItemStore()
  const { dungeons: savedDungeons } = useDungeonStore()
  const { savedWorlds } = useWorldBuilderStore()
  const { savedCommunities } = useCommunityStore()
  
  // コンポーネントマウント時に重複IDをチェックして修正
  useEffect(() => {
    const hasDuplicates = fixDuplicateIds()
    if (hasDuplicates) {
      console.log('エネミーIDの重複を修正しました')
    }
  }, [fixDuplicateIds])
  
  // 初期ロード時に既存の参照データからコンテキストを構築
  useEffect(() => {
    if (input.references && Object.keys(input.references).length > 0) {
      const context = buildCompleteContext(input.references)
      if (context && !input.referenceContext) {
        onChange({
          ...input,
          referenceContext: context,
        })
      }
    }
  }, [])
  
  const buildCompleteContext = (refs: any) => {
    const contexts: string[] = []
    
    // 世界観
    if (refs?.savedWorldId) {
      const world = savedWorlds.find(w => w.id === refs.savedWorldId)
      if (world) {
        contexts.push(`【世界観設定から共同体を構築】
名前: ${world.result.name || '未命名'}
コンセプト: ${world.result.concept || ''}
規模: ${world.result.worldScale || ''}
ジャンル: ${world.result.genres?.join('、') || ''}
キーワード: ${world.input.keywords?.join('、') || ''}`)
      }
    }
    
    // プロット
    if (refs?.savedPlotId) {
      const plot = savedPlots.find(p => p.id === refs.savedPlotId)
      if (plot) {
        contexts.push(generatePlotContext(plot))
      }
    }
    
    // キャラクター
    if (refs?.savedCharacterIds && refs.savedCharacterIds.length > 0) {
      const selectedChars = characters.filter(c => refs.savedCharacterIds?.includes(c.id))
      if (selectedChars.length > 0) {
        contexts.push(`【キャラクター（${selectedChars.length}名）から共同体を構築】`)
        selectedChars.forEach(char => {
          contexts.push(generateCharacterContext(char))
        })
      }
    }
    
    // エネミー
    if (refs?.savedEnemyIds && refs.savedEnemyIds.length > 0) {
      const selectedEnemies = savedEnemies.filter(e => refs.savedEnemyIds?.includes(e.id))
      if (selectedEnemies.length > 0) {
        contexts.push(`【エネミー・脅威（${selectedEnemies.length}体）から共同体を構築】`)
        selectedEnemies.forEach(enemy => {
          contexts.push(generateEnemyContext(enemy))
        })
      }
    }
    
    // アイテム
    if (refs?.savedItemIds && refs.savedItemIds.length > 0) {
      const selectedItems = savedItems.filter(i => refs.savedItemIds?.includes(i.id))
      if (selectedItems.length > 0) {
        contexts.push(`【アイテム（${selectedItems.length}個）から共同体を構築】`)
        selectedItems.forEach(item => {
          contexts.push(generateItemContext(item))
        })
      }
    }
    
    // ダンジョン
    if (refs?.savedDungeonIds && refs.savedDungeonIds.length > 0) {
      const selectedDungeons = savedDungeons.filter(d => refs.savedDungeonIds?.includes(d.id))
      if (selectedDungeons.length > 0) {
        contexts.push(`【ダンジョン（${selectedDungeons.length}箇所）から共同体を構築】`)
        selectedDungeons.forEach(dungeon => {
          contexts.push(generateDungeonContext(dungeon))
        })
      }
    }
    
    return contexts.join('\n\n')
  }
  
  const handleReferenceChange = (type: string, value: string | string[]) => {
    const references = { ...input.references }
    
    // 参照データを更新
    const newReferences = {
      ...references,
      [type === 'world' ? 'savedWorldId' :
       type === 'plot' ? 'savedPlotId' : 
       type === 'characters' ? 'savedCharacterIds' :
       type === 'enemies' ? 'savedEnemyIds' :
       type === 'items' ? 'savedItemIds' :
       'savedDungeonIds']: value,
    }
    
    // 完全なコンテキストを構築（更新された参照を使用）
    const newContext = buildCompleteContext(newReferences)
    
    onChange({
      ...input,
      references: newReferences,
      referenceContext: newContext,
    })
  }
  
  return (
    <div className="space-y-4">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          保存済みの他のジェネレータのデータから共同体を構築します。
          選択したデータを分析して、AIが最適な共同体パラメータを自動決定します。
        </AlertDescription>
      </Alert>
      
      {savedWorlds.length > 0 && (
        <div>
          <Label>世界観設定から生成</Label>
          <Select
            value={input.references?.savedWorldId || ''}
            onValueChange={(value) => handleReferenceChange('world', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="世界観を選択..." />
            </SelectTrigger>
            <SelectContent>
              {savedWorlds.map((world) => (
                <SelectItem key={world.id} value={world.id}>
                  {world.result.name || '未命名'} - {world.result.concept}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {savedPlots.length > 0 && (
        <div>
          <Label>プロットから生成</Label>
          <Select
            value={input.references?.savedPlotId || ''}
            onValueChange={(value) => handleReferenceChange('plot', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="プロットを選択..." />
            </SelectTrigger>
            <SelectContent>
              {savedPlots.map((plot) => (
                <SelectItem key={plot.id} value={plot.id}>
                  {plot.title} - {plot.result.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {characters.length > 0 && (
        <div>
          <Label>キャラクターから生成（複数選択可）</Label>
          <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
            {characters.map((char) => (
              <label
                key={char.id}
                className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-accent"
              >
                <input
                  type="checkbox"
                  checked={input.references?.savedCharacterIds?.includes(char.id) || false}
                  onChange={(e) => {
                    const ids = input.references?.savedCharacterIds || []
                    const newIds = e.target.checked
                      ? [...ids, char.id]
                      : ids.filter(id => id !== char.id)
                    handleReferenceChange('characters', newIds)
                  }}
                />
                <span className="text-sm">{char.title}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      
      {savedEnemies.length > 0 && (
        <div>
          <Label>エネミー・怪異から生成（複数選択可）</Label>
          <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
            {savedEnemies.map((enemy) => (
              <label
                key={enemy.id}
                className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-accent"
              >
                <input
                  type="checkbox"
                  checked={input.references?.savedEnemyIds?.includes(enemy.id) || false}
                  onChange={(e) => {
                    const ids = input.references?.savedEnemyIds || []
                    const newIds = e.target.checked
                      ? [...ids, enemy.id]
                      : ids.filter(id => id !== enemy.id)
                    handleReferenceChange('enemies', newIds)
                  }}
                />
                <span className="text-sm">{enemy.title}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      
      {savedItems.length > 0 && (
        <div>
          <Label>アイテムから生成（複数選択可）</Label>
          <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
            {savedItems.map((item) => (
              <label
                key={item.id}
                className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-accent"
              >
                <input
                  type="checkbox"
                  checked={input.references?.savedItemIds?.includes(item.id) || false}
                  onChange={(e) => {
                    const ids = input.references?.savedItemIds || []
                    const newIds = e.target.checked
                      ? [...ids, item.id]
                      : ids.filter(id => id !== item.id)
                    handleReferenceChange('items', newIds)
                  }}
                />
                <span className="text-sm">{item.title}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      
      {savedDungeons.length > 0 && (
        <div>
          <Label>ダンジョンから生成（複数選択可）</Label>
          <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
            {savedDungeons.map((dungeon) => (
              <label
                key={dungeon.id}
                className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-accent"
              >
                <input
                  type="checkbox"
                  checked={input.references?.savedDungeonIds?.includes(dungeon.id) || false}
                  onChange={(e) => {
                    const ids = input.references?.savedDungeonIds || []
                    const newIds = e.target.checked
                      ? [...ids, dungeon.id]
                      : ids.filter(id => id !== dungeon.id)
                    handleReferenceChange('dungeons', newIds)
                  }}
                />
                <span className="text-sm">{dungeon.title}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      
      {/* 関連コミュニティ連携 */}
      <CommunityContextForm
        input={input}
        onChange={onChange}
        savedCommunities={savedCommunities}
      />
      
      <div className="space-y-2 border-t pt-4">
        <div className="flex items-center space-x-2">
          <Settings className="h-4 w-4" />
          <Label>追加設定（オプション）</Label>
        </div>
        <Textarea
          value={input.additionalRequirements || ''}
          onChange={(e) => onChange({ ...input, additionalRequirements: e.target.value })}
          placeholder="例：
・山間部の隠れ里として設定
・魔法使いが多く住む学術都市
・交易路の要衝として繁栄
・古代遺跡の上に建設された都市
・異種族が共存する多文化都市"
          rows={4}
        />
        <p className="text-xs text-muted-foreground">
          参照データに加えて、共同体に含めたい要素や詳細な指示を追加できます
        </p>
      </div>
    </div>
  )
}